import { useMutation, useQueryClient } from '@tanstack/react-query'
import { internal_backend } from '~/internal-backend'

import { toast } from 'sonner'
import { GET_SHOPIFY_PRODUCTS_QUERY_KEY } from './useGetShopifyProducts'
import { usePatchModels } from '~/features/models/models-list/hooks/usePatchModels'
import { z } from 'zod'
import { FetchedInventory, GET_INVENTORY_QUERY_KEY } from '~/features/hooks/useGetInventory'
import { groupBy, mapToObj } from 'remeda'
import { getInventoryToUpdate, getVariantsFromCreatedProducts } from '../utils'
import { ModelV2, ProductType } from '~/supabase/types'
import { supabaseApi } from '~/supabase/api'
import { ShopifyCreateProductParams } from '../../../../shared/schemas/shopify'

export const useCreateShopifyProducts = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: patchModels } = usePatchModels()

  return useMutation({
    mutationKey: ['create-shopify-product'],
    mutationFn: async (
      products: Array<
        ShopifyCreateProductParams & {
          inventory: FetchedInventory[]
          productType: ProductType
          price: number
        }
      >
    ) => {
      const productMap = mapToObj(products, (product) => [product.product.title, product])
      const response = await Promise.all(
        products.map(({ product, media }) =>
          internal_backend.create_product.request({ product, media })
        )
      )

      const parsed = z.array(internal_backend.create_product.responseSchema).safeParse(response)

      if (!parsed.success) {
        toast.error('Failed to parse Shopify products')
        throw new Error(parsed.error.message)
      }

      const productsToUpdate = parsed.data.flatMap((model, index) => {
        const productData = products[index]
        if (!productData) return []
        const id = productData.product.metafields.find((m) => m.key === 'id')?.value
        const numberId = id ? parseInt(id) : null
        if (!numberId) return []

        const price = productMap[model.product.title]?.price
        if (price === undefined) return []

        return {
          id: numberId,
          stock_code: model.product.title,
          shopify_product_id: model.product.id,
          price,
          status_v2: 'in_eshop',
          product_type: productData.productType,
        } as const satisfies Partial<ModelV2>
      })

      if (productsToUpdate.length !== products.length) {
        throw new Error(
          'Failed to patch supabase products. Some of the products created in shopify came back wrong.'
        )
      }
      const productInventoryMap = mapToObj(
        products,
        (product) =>
          [
            product.product.title,
            groupBy(
              product.inventory.filter((inventory) => inventory.status === 'available'),
              (inventory) => inventory.size
            ),
          ] as const
      )

      const variantsOfProducts = getVariantsFromCreatedProducts(
        productsToUpdate,
        productInventoryMap
      )

      if (variantsOfProducts.length !== productsToUpdate.length) {
        throw new Error(
          'Failed to create Shopify variants. Some of the products created in shopify came back wrong.'
        )
      }

      const variantResponse = await Promise.all(
        variantsOfProducts.map(({ productId, variants, strategy }) => {
          return internal_backend.create_variants.request({
            productId,
            variants,
            strategy,
          })
        })
      )
      console.log('variantResponse', variantResponse)
      // TODO return Quantity for 0 couldn't be set because the location was deleted.
      const parsedVariantResponse = z
        .array(internal_backend.create_variants.responseSchema)
        .safeParse(variantResponse)

      if (!parsedVariantResponse.success) {
        throw new Error(parsedVariantResponse.error.message)
      }

      const inventoryToUpdate = getInventoryToUpdate(
        parsedVariantResponse.data,
        productInventoryMap
      )

      await Promise.all(
        inventoryToUpdate.map((inventory) =>
          supabaseApi.from('inventory_v2').update(inventory).eq('id', inventory.id)
        )
      )

      return await patchModels(productsToUpdate)
    },
    onSuccess: () => {
      toast.success('Product created successfully')
      queryClient.invalidateQueries({ queryKey: [GET_SHOPIFY_PRODUCTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
    },
    onError: (error) => {
      console.error(error)
      toast.error(`Failed to create product: ${error.message}`)
    },
  })
}
